<template>
	<v-app>
		<v-system-bar v-if="impersonationMode" color="red" height="40px" class="justify-center">
			<v-toolbar-title class="white--text" style="font-weight: bold">Impersonation mode is enabled</v-toolbar-title>
			<v-btn small elevation="2" class="black--text ml-5" color="gray" rounded @click="disableImpersonation">Disable</v-btn>
			<v-btn small elevation="2" class="black--text ml-5" color="gray" rounded @click="handleVerificationFormClick">{{ pageLabelDictionary[currentPage] }}</v-btn>
		</v-system-bar>
		<v-row v-if="type === 'demo'">
			<v-col cols="6">
				<v-select label="Select Industry" :items="industriesList" single-line @change="changeIndustry"></v-select>
			</v-col>
			<v-col v-if="user.locale === 'professional'" cols="6">
				<v-select label="Select Profession" :items="professionsList" single-line @change="changeProfession"></v-select>
			</v-col>
		</v-row>
		<locum-preview-popup :show="showLocumPreviewModal"></locum-preview-popup>
		<component :is="$route.meta.layout || 'div'">
			<router-view />
		</component>

		<wallet-dialog ref="walletDialog"></wallet-dialog>
		<pricing-dialog ref="PricingDialog"></pricing-dialog>
		<MessageDialog ref="MessageDialog"></MessageDialog>
	</v-app>
</template>

<script>
import store from '@/store';
import router from '@/router';
import { bus } from '@/main';
import LocumPreviewPopup from '@/components/ui/dashboard/locum/cards/LocumPreviewPopup';
import WalletDialog from '@/components/custom/WalletDialog';
import { mapActions, mapGetters } from 'vuex';
import { setImpersonatedUser, getImpersonationCache } from '@utils';
import { Tracking } from '@utils/tracking';
import PricingDialog from '@/components/custom/PricingDialog.vue';
import MessageDialog from '@/components/custom/MessageDialog.vue';

export default {
	name: 'App',
	components: { LocumPreviewPopup, WalletDialog, PricingDialog, MessageDialog },
	data() {
		return {
			impersonationMode: getImpersonationCache().impersonationMode,
			currentPage: window.location.pathname.includes('/verification') ? 'verification' : 'dashboard',
			pageLabelDictionary: {
				verification: 'Go to Dashboard',
				dashboard: 'View Verification Form',
			},
			showLocumPreviewModal: false,
		};
	},
	computed: {
		...mapGetters('tenant', ['tenant']),

		industriesOptions() {
			return store.getters.configs.industries || {};
		},
		industriesList() {
			return Object.keys(this.industriesOptions);
		},
		professionsList() {
			return this.industriesOptions[this.user.industry];
		},
		user() {
			return store.getters.user || {};
		},
		company() {
			return store.getters.company || {};
		},
		// Used to determine if this is a demo user
		type() {
			return this.user.locale === 'company' ? this.company.type : this.user.type;
		},

		tawkActive() {
			return (
				this.tenant &&
				this.tenant.settings &&
				this.tenant.settings.workflow &&
				this.tenant.settings.workflow.support &&
				this.tenant.settings.workflow.support.tawk &&
				this.tenant.settings.workflow.support.tawk.active
			);
		},
	},
	mounted() {
		store.dispatch('UpdateProvincesRegions');
		store.dispatch('UpdatePublicHolidays');
		Tracking.updateMixpanel();
		bus.$on('impersonationModeChange', data => {
			this.impersonationMode = data;
		});

		bus.$on('navToDashboard', () => {
			this.currentPage = 'dashboard';
		});

		bus.$on('show-locum-preview-popup', () => {
			this.showLocumPreviewModal = true;
		});

		bus.$on('dismiss-locum-preview-popup', () => {
			this.showLocumPreviewModal = false;
		});

		bus.$on('show-wallet-dialog', () => {
			this.$refs.walletDialog.open();
		});

		bus.$on('dismiss-wallet-dialog', () => {
			this.$refs.walletDialog.close();
		});

		bus.$on('show-pricing-dialog', () => {
			this.$refs.PricingDialog.open();
		});

		bus.$on('dismiss-pricing-dialog', () => {
			this.$refs.PricingDialog.close();
		});

		bus.$on('show-message-dialog', (receiverDetails, sentByID) => {
			this.$refs.MessageDialog.open(receiverDetails, sentByID);
		});

		this.setTawkUser();
	},
	methods: {
		...mapActions('professional', ['LoadTawkHash']),
		...mapActions('company', ['LoadCompanyTawkHash']),
		changeIndustry(newIndustry) {
			store.commit('CHANGE_INDUSTRY', newIndustry);
		},
		changeProfession(newProfession) {
			store.commit('CHANGE_PROFESSION', newProfession);
		},
		async disableImpersonation() {
			await setImpersonatedUser();
			router.push('/admin/dashboard');
		},
		async setTawkUser() {
			if (!this.tawkActive) {
				window.Tawk_API.hideWidget();
				return;
			}

			window.Tawk_API = window.Tawk_API || {};
			window.Tawk_API.onLoad = async function () {
				window.Tawk_API.setAttributes(
					{
						name: this.user.name || this.user.first_name,
						email: this.user.email,
						hash: this.user.locale === 'company' ? await this.LoadCompanyTawkHash() : await this.LoadTawkHash(),
					},
					function (error) {
						if (error != undefined) console.log(error);
					}
				);
			};
		},
		handleVerificationFormClick() {
			const type = this.user.locale === 'company' ? 'practice' : 'locum';
			this.currentPage = this.currentPage === 'dashboard' ? 'verification' : 'dashboard';
			this.$router.push(`/${type}/${this.currentPage}`);
		},
	},
};
</script>

<style lang="scss">
@import 'styles/app.scss';
@import 'styles/variables.scss';
</style>
